import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { replaceStringValues } from '../Services/translation';

// parse file size to kb or mb
const parseFileSize = (size) => {
  if (size < 1024) {
    return size + ' bytes';
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(2) + ' KB';
  } else {
    return (size / 1024 / 1024).toFixed(2) + ' MB';
  }
};

// parse size limit from string to number depending on the unit
const parseSizeLimit = (sizeLimit) => {
  const size = sizeLimit.substring(0, sizeLimit.length - 2);
  const unit = sizeLimit
    .substring(sizeLimit.length - 2, sizeLimit.length)
    .toUpperCase();
  if (unit === 'MB') {
    return size * 1024 * 1024;
  } else if (unit === 'KB') {
    return size * 1024;
  } else {
    return size;
  }
};

const DEFAULT_FILE_SIZE_ERROR = 'File size cannot exceed {{fileSizeLimit}}.';
const DEFAULT_MAX_FILES_ERROR = 'You can only upload {{numOfFiles}} files.';
const DEFAULT_TOTAL_FILE_SIZE_ERROR =
  'Total file size cannot exceed {{totalFileSizeLimit}}.';

export const FileUploader = ({
  onFileSelectSuccess,
  buttonLabel,
  fileSizeError,
  maxFilesError,
  totalFileSizeError,
  fileSizeLimit = '1MB',
  totalFileSizeLimit = '5MB',
  maxFiles = 5,
  inputName,
  inputId,
}) => {
  const fileInput = useRef(null);

  const buttonLabelString = buttonLabel || 'Upload file';

  const [files, setFiles] = useState(new DataTransfer().files);
  const [error, setError] = useState(null);

  const fileInputClick = (e) => {
    e.preventDefault();
    fileInput.current && fileInput.current.click();
  };

  const removeFile = (file) => {
    if (!fileInput.current) return;
    const currentFiles = fileInput.current.files;
    const newFiles = new DataTransfer();
    for (let i = 0; i < currentFiles.length; i++) {
      if (currentFiles[i].name !== file.name) {
        newFiles.items.add(currentFiles[i]);
      }
    }
    fileInput.current.files = newFiles.files;
    setFiles(newFiles.files);
  };

  const handleFileInput = (e) => {
    // handle validations
    const filesData = e.target.files;

    const hasPreviousFiles = files.length > 0;

    const data = new DataTransfer();

    let error = null;

    if (hasPreviousFiles) {
      // ? if there are previous files
      // ? add previous files to data
      Array.from(files).forEach((file) => {
        data.items.add(file);
      });
    }

    const previousFileSize = Array.from(data.files).reduce((acc, file) => {
      return acc + file.size;
    }, 0);

    const filesDataTotalSize = Array.from(filesData).reduce((acc, file) => {
      return acc + file.size;
    }, 0);

    // ! check if total file size exceeds limit
    const hasTotalSizeError =
      previousFileSize + filesDataTotalSize > parseSizeLimit(totalFileSizeLimit);

    // ! check if file amount exceeds limit
    const hasFileAmountError = filesData.length + data.files.length > maxFiles;

    if (hasFileAmountError) {
      // ? set error message if file amount exceeds limit
      error = replaceStringValues(maxFilesError || DEFAULT_MAX_FILES_ERROR, {
        numOfFiles: maxFiles,
      });
    }

    if (hasTotalSizeError) {
      // ? set error message if total file size exceeds limit
      error = replaceStringValues(
        totalFileSizeError || DEFAULT_TOTAL_FILE_SIZE_ERROR,
        {
          fileSizeLimit: totalFileSizeLimit,
        }
      );
    }

    // ! check if file size exceeds limit
    const hasSizeLimitError = Array.from(filesData).some(
      (file) => file.size > parseSizeLimit(fileSizeLimit)
    );

    if (hasSizeLimitError) {
      // ? set error message if file size exceeds limit
      error = replaceStringValues(fileSizeError || DEFAULT_FILE_SIZE_ERROR, {
        fileSizeLimit,
      });
    }

    if (!hasTotalSizeError && !hasFileAmountError && !hasSizeLimitError) {
      // ? add new files to data
      // ? if there are no errors
      Array.from(filesData).forEach((file) => {
        if (Array.from(data.files).find((f) => f.name === file.name)) return; // ? duplicate check
        data.items.add(file);
      });
    }

    const newFiles = data.files;

    if (error) {
      e.target.files = newFiles; // ? reset files on the input, if there are any previous files they will be added
      setError(error);
      return;
    } else {
      setError(null);

      e.target.files = newFiles; // ? set files on the input
      setFiles(newFiles); // ? set files on the state
    }
  };

  useEffect(() => {
    onFileSelectSuccess && onFileSelectSuccess(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <>
      {files && files.length > 0 && (
        <ul className="file-uploader__files">
          {Array.from(files).map((file, index) => (
            <li key={index} className="file-uploader__file">
              <button
                className="tiny alert button margin-right-1 padding-0"
                type="button"
                onClick={() => removeFile(file)}
              >
                X
              </button>
              <span>{file.name}</span>
              <span>{parseFileSize(file.size)}</span>
            </li>
          ))}
        </ul>
      )}
      <label className="file-uploader">
        <input
          type="file"
          onChange={handleFileInput}
          ref={fileInput}
          className="file-uploader__input"
          multiple
          name={inputName || 'file'}
          {...{
            ...(inputId && { id: inputId }),
          }}
        />

        {error && <div className="file-uploader__error">{error}</div>}

        <button
          onClick={fileInputClick}
          className="button file-uploader__button"
          type="button"
        >
          {buttonLabelString}
        </button>
      </label>
    </>
  );
};

FileUploader.propTypes = {
  onFileSelectSuccess: PropTypes.func,
  buttonLabel: PropTypes.string,
  fileSizeError: PropTypes.string,
  maxFilesError: PropTypes.string,
  totalFileSizeError: PropTypes.string,
  fileSizeLimit: PropTypes.string,
  totalFileSizeLimit: PropTypes.string,
  maxFiles: PropTypes.number,
  inputName: PropTypes.string,
  inputId: PropTypes.string,
};
